import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import ChaosHeader from "@/components/ChaosHeader";
import AppFooter from "@/components/AppFooter";
import { useAuth } from "@/contexts/AuthContext";

const TALLY_URL = "https://tally.so/r/RGrZ8v";
const TALLY_EMBED_SRC = `${TALLY_URL}?alignLeft=1`;
const SUPPORT_EMAIL = "support@truthscanhub.com";
const STORAGE_KEY = "truth-scan-hub-review-submitted";

const ReviewPage = () => {
  const { user, status } = useAuth();

  // Per-user submission lock (anon users get the device-level lock).
  const userKey = user?.user_id || "anon";
  const fullKey = `${STORAGE_KEY}:${userKey}`;

  const [hasSubmittedReview, setHasSubmittedReview] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(fullKey) === "1";
    } catch {
      return false;
    }
  });

  // Re-read storage whenever the active user identity changes (login/logout).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setHasSubmittedReview(window.localStorage.getItem(fullKey) === "1");
    } catch {
      setHasSubmittedReview(false);
    }
  }, [fullKey]);

  // Listen for the Tally postMessage broadcast emitted when the form is
  // submitted inside the iframe. Tally posts a JSON-encoded payload with
  // `event: "Tally.FormSubmitted"`. We also persist on `pagehide` so a
  // user who completes the form and navigates away still gets locked.
  useEffect(() => {
    if (hasSubmittedReview) return undefined;

    const lock = () => {
      try {
        window.localStorage.setItem(fullKey, "1");
      } catch {
        /* storage blocked */
      }
      setHasSubmittedReview(true);
    };

    const onMessage = (event) => {
      if (!event?.data) return;
      let payload = event.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }
      if (
        payload?.event === "Tally.FormSubmitted" ||
        payload?.eventName === "Tally.FormSubmitted"
      ) {
        lock();
      }
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [fullKey, hasSubmittedReview]);

  return (
    <div
      data-testid="app-root"
      className="chaos-grain chaos-bg-gradient min-h-[100dvh] w-full text-[#f1f5f9] flex flex-col"
    >
      <ChaosHeader showCredits={status === "authed"} />

      <div
        data-testid="review-tally-wrapper"
        className="flex-1 w-full max-w-4xl mx-auto px-3 sm:px-6 lg:px-10 pb-6"
        style={{
          paddingTop: "max(12px, env(safe-area-inset-top))",
        }}
      >
        <div className="flex items-center justify-between gap-3 mb-3 sm:mb-5">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#cbd5e1] hover:text-[#FF0033] transition-colors"
          >
            <ArrowLeft size={13} /> Back to Terminal
          </Link>
          {!hasSubmittedReview && (
            <a
              href={TALLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-tally-external"
              className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#cbd5e1] hover:text-[#FF0033] transition-colors"
            >
              Open externally ↗
            </a>
          )}
        </div>

        <h1 data-testid="review-heading" className="sr-only">
          Submit Verification Review
        </h1>

        {hasSubmittedReview ? (
          <div
            data-testid="review-submitted-card"
            className="border border-emerald-500/30 bg-emerald-500/[0.06] p-8 sm:p-12 text-center max-w-2xl mx-auto"
          >
            <div className="inline-flex w-14 h-14 items-center justify-center border border-emerald-400 text-emerald-400 mb-6">
              <CheckCircle2 size={26} />
            </div>
            <h2 className="font-mono text-xl sm:text-2xl uppercase tracking-[0.18em] text-[#f1f5f9] mb-3">
              Thank you
            </h2>
            <p className="text-[#cbd5e1] text-[15px] leading-relaxed mb-6">
              Your verification node review has been logged. To submit
              further platform requests or bug reports, please contact our
              support desk directly via email.
            </p>
            <button
              type="button"
              disabled
              aria-disabled="true"
              data-testid="support-mailto"
              title="Email support is launching soon."
              className="inline-flex items-center gap-2 border border-[#FF0033]/40 bg-[#FF0033]/[0.06] text-[#FF0033]/70 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] cursor-not-allowed opacity-80"
            >
              <Mail size={13} /> Contact Support
              <span className="ml-1 text-[9px] tracking-[0.18em] text-[#cbd5e1]/70">(Coming Soon)</span>
            </button>
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-[#94a3b8]">
              {SUPPORT_EMAIL} (Coming Soon)
            </p>
          </div>
        ) : (
          <div className="border border-white/[0.08] bg-white overflow-hidden">
            {/* white wrapper so the embedded Tally form maintains its own
                light card aesthetic regardless of the active workspace skin */}
            <iframe
              data-testid="tally-iframe"
              src={TALLY_EMBED_SRC}
              title="Submit Verification Review"
              loading="lazy"
              allow="payment"
              className="w-full block border-0 bg-white"
              style={{ height: "calc(100dvh - 180px)", minHeight: 560 }}
            />
          </div>
        )}
      </div>
      <AppFooter />
    </div>
  );
};

export default ReviewPage;
